/**
 * Posture Analyses API - Client-Side Service
 * 
 * All posture analysis database operations go through server-side Express API.
 * Client-side code MUST NOT access the database directly.
 */
import type { PostureAnalysis } from '@/lib/types';
import { apiFetch, pollEndpoint } from '@/lib/api-client';

// Response types
interface CreateAnalysisResponse {
  success: boolean;
  analysisId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

/**
 * Create a new posture analysis for a client
 */
export async function createPostureAnalysis(
  clientId: string,
  analysis: Omit<PostureAnalysis, 'id'>
): Promise<string> {
  const response = await apiFetch<CreateAnalysisResponse>(
    `/clients/${clientId}/posture-analyses`,
    {
      method: 'POST',
      body: JSON.stringify(analysis),
    }
  );
  return response.analysisId;
}

/**
 * Get a single posture analysis by ID
 */
export async function getPostureAnalysis(
  clientId: string,
  analysisId: string
): Promise<PostureAnalysis | null> {
  try {
    const analysis = await apiFetch<PostureAnalysis>(
      `/clients/${clientId}/posture-analyses/${analysisId}`
    );
    return analysis;
  } catch (error: any) {
    if (error.message?.includes('not-found') || error.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Get all posture analyses for a client
 */
export async function getClientPostureAnalyses(clientId: string): Promise<PostureAnalysis[]> {
  return apiFetch<PostureAnalysis[]>(`/clients/${clientId}/posture-analyses`);
}

/**
 * Update a posture analysis
 */
export async function updatePostureAnalysis(
  clientId: string,
  analysisId: string,
  updates: Partial<PostureAnalysis>
): Promise<void> {
  await apiFetch<UpdateResponse>(
    `/clients/${clientId}/posture-analyses/${analysisId}`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

/**
 * Delete a posture analysis
 */
export async function deletePostureAnalysis(
  clientId: string,
  analysisId: string
): Promise<void> {
  await apiFetch<UpdateResponse>(
    `/clients/${clientId}/posture-analyses/${analysisId}`,
    {
      method: 'DELETE',
    }
  );
}

/**
 * Subscribe to posture analyses with polling for real-time updates
 * Returns cleanup function to stop polling
 */
export function subscribeToClientPostureAnalyses(
  clientId: string,
  callback: (analyses: PostureAnalysis[]) => void,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  return pollEndpoint<PostureAnalysis[]>(
    `/clients/${clientId}/posture-analyses`,
    callback,
    intervalMs,
    onError
  );
}
